// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed } from '@ember/object';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { bool } from '@ember/object/computed';
import { service } from '@ember/service';
import { camelize } from '@ember/string';
import Model, { attr, belongsTo, hasMany, type AsyncHasMany, type HasMany } from '@ember-data/model';
import { apiAction } from '@mainmatter/ember-api-actions';
import type { Type } from '@warp-drive/core-types/symbols';
import { format, toZonedTime } from 'date-fns-tz';
import type Store from 'ember-data/store';
import type Agreement from 'invite-sign/models/agreement';
import type DocumentUploadRequirement from 'invite-sign/models/document-upload-requirement';
import type GroupInviteLink from 'invite-sign/models/group-invite-link';
import type Location from 'invite-sign/models/location';
import type Questionnaire from 'invite-sign/models/questionnaire';
import type SignInField from 'invite-sign/models/sign-in-field';
import type UserDocumentTemplate from 'invite-sign/models/user-document-template';
import type LocalizationService from 'invite-sign/services/localization';
import type { SingleResponse } from 'jsonapi/response';

type RawInviteStatusResponse = {
  data: {
    attributes: {
      'approval-status': string;
      'deny-message': string;
      'deny-message-markdown': string;
      'employee-screening-flow': boolean;
    };
  };
};

type RawUnfinishedAgreementsResponse = {
  agreements: Array<{ id: string; type: 'agreements' }>;
};

export type InviteStatus = {
  approvalStatus: string;
  denyMessage: string;
  denyMessageMarkdown: string;
  employeeScreeningFlow: boolean;
};

export default class Invite extends Model {
  declare [Type]: 'invite';

  @service declare localization: LocalizationService;
  @service declare store: Store;

  @belongsTo('location', { async: false, inverse: null }) declare location: Location;
  @belongsTo('group-invite-link', { async: false, inverse: null }) declare groupInviteLink: GroupInviteLink | null;
  @hasMany('agreement', { async: true, inverse: 'invite' }) declare agreements: AsyncHasMany<Agreement>;
  @hasMany('questionnaire', { async: false, inverse: null }) declare questionnaires: HasMany<Questionnaire>;
  @hasMany('sign-in-field', { async: false, inverse: 'invite' }) declare signInFields: HasMany<SignInField>;
  @hasMany('user-document-template', { async: true, inverse: null })
  declare userDocumentTemplates: AsyncHasMany<UserDocumentTemplate>;
  @hasMany('document-upload-requirement', { async: false, inverse: null })
  declare documentUploadRequirements: HasMany<DocumentUploadRequirement>;

  @attr('number', { defaultValue: 0 }) declare additionalGuests: number;
  @attr('boolean', { defaultValue: false }) declare additionalGuestsEnabled: boolean;
  @attr('string') declare approvalStatus: string;
  @attr('string') declare denyMessage: string;
  @attr('string') declare editToken: string;
  @attr('boolean') declare employeeScreeningFlow: boolean;
  @attr('string') declare entryId: string | null;
  @attr('date') declare expectedArrivalTime: Date;
  @attr('string') declare headerText: string;
  @attr('boolean', { defaultValue: false }) declare idScanEnabled: boolean;
  @attr('string') declare idScanSessionId: string | null;
  @attr('boolean', { defaultValue: false }) declare skipIdScan: boolean;
  @attr('string') declare inviteId: string | null;
  @attr({ defaultValue: () => ({ 'place-id': '' }) }) declare locality: { 'place-id'?: string };
  @attr() declare photo: unknown;
  @attr('boolean', { defaultValue: false }) declare photoEnabled: boolean;
  @attr('string') declare purposeOfVisitName: string | null;
  @attr('string') declare qrCodeBase64: string;
  @attr('string') declare recurringInviteId: string | null;
  @attr() declare thumbnails: { original: string } | null;
  @attr('boolean', { defaultValue: false }) declare visitWorkflowScreened: boolean;

  @bool('qrCodeBase64') declare hasQrCode: boolean;

  get shouldHideBack(): boolean {
    return this.visitWorkflowScreened && !this.employeeScreeningFlow;
  }

  get formattedTime(): string {
    const expectedArrivalTime = toZonedTime(this.expectedArrivalTime, this.location?.timezone);
    const token = this.employeeScreeningFlow ? 'eeee, PPP' : 'PPPp';

    return format(expectedArrivalTime, token, {
      locale: this.localization.dateFnsLocale,
      timeZone: this.location?.timezone,
    });
  }

  get stringifiedTime(): string {
    const expectedArrivalTime = toZonedTime(this.expectedArrivalTime, this.location?.timezone);
    return format(expectedArrivalTime, 'PPPPp', {
      locale: this.localization.dateFnsLocale,
      timeZone: this.location?.timezone,
    });
  }

  @computed('signInFields.@each.type')
  get host(): string | undefined {
    const field = this.signInFields.find((field) => field.type === 'Host');
    return field?.value;
  }

  @computed('signInFields.@each.type')
  get fullName(): string | undefined {
    const field = this.signInFields.find((field) => field.type === 'Name');
    return field?.value;
  }

  @computed('signInFields.@each.type')
  get email(): string | undefined {
    const field = this.signInFields.find((field) => field.type === 'Email');
    return field?.value;
  }

  @computed('signInFields.@each.{value,isPurposeOfVisit}', 'purposeOfVisitName')
  get purposeOfVisit(): string | undefined {
    if (this.purposeOfVisitName) {
      return this.purposeOfVisitName;
    }
    const field = this.signInFields.find((field) => field.isPurposeOfVisit);
    return field?.value;
  }

  async finalize(): Promise<void> {
    const response = await apiAction<SingleResponse<Invite>>(this, { method: 'POST', path: 'finalize' });
    // @ts-expect-error - ember-data's types for `pushPayload` don't match the documentation. It works to only pass the payload, not a model name, but the types don't define that signature.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload(response);
  }

  get hasValidSignInFields(): boolean {
    return this.signInFields
      .map((field) => !field.renderable || !field.isVisible || field.validate())
      .every((value) => value);
  }

  async status(): Promise<InviteStatus> {
    const response = await apiAction<RawInviteStatusResponse>(this, { method: 'GET', path: 'invite-status' });
    const attributes = response.data.attributes;

    return <InviteStatus>Object.keys(attributes).reduce((total, key) => {
      // @ts-expect-error -- we know that `camelize(key)` is a valid key but TS doesn't
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      total[camelize(key)] = attributes[key];
      return total;
    }, {});
  }

  toJSON(): object {
    const {
      data: { attributes },
    } = <SingleResponse<Invite>>this.serialize();

    return Object.entries(attributes).reduce((carry, [key, value]) => {
      // @ts-expect-error -- we know that `camelize(key)` is a valid key but TS doesn't
      carry[camelize(key)] = value;

      return carry;
    }, {});
  }

  async unfinishedAgreements(): Promise<Agreement[]> {
    const response = await apiAction<RawUnfinishedAgreementsResponse>(this, {
      method: 'GET',
      path: 'unfinished-agreements',
    });
    return <Agreement[]>this.store.push({
      data: response.agreements.map((agreement) => ({
        id: agreement.id,
        type: 'agreement',
      })),
    });
  }
}
